// ============================================================
// THE COMPANY — Prompt / briefing template generators
// Pure string interpolation — NO LLM calls.
// ============================================================

import { COMPANY_NAME } from "./constants";
import type { Role, Decision, Conversation } from "./types";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build an indented org-chart text from the flat roles list. */
function buildOrgChartText(roles: Role[]): string {
  const byParent = new Map<string | null, Role[]>();
  for (const r of roles) {
    const key = r.parentId ?? "__root__";
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(r);
  }

  const lines: string[] = [];
  const visited = new Set<string>();
  const MAX_DEPTH = 20;

  function walk(parentId: string | null, depth: number) {
    if (depth > MAX_DEPTH) return;
    const children = byParent.get(parentId ?? "__root__") ?? [];
    children
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .forEach((r) => {
        if (visited.has(r.id)) return;
        visited.add(r.id);
        const indent = "  ".repeat(depth);
        lines.push(`${indent}- ${r.name}, ${r.title}`);
        walk(r.id, depth + 1);
      });
  }

  walk(null, 0);
  return lines.join("\n");
}

/** Build the reporting-line sentence for a role. */
function buildReportingLine(role: Role, allRoles: Role[]): string {
  const parent = allRoles.find((r) => r.id === role.parentId);
  const children = allRoles.filter((r) => r.parentId === role.id);

  const parts: string[] = [];
  if (parent) {
    parts.push(`You report to ${parent.name} (${parent.title}).`);
  } else {
    parts.push("You are at the top of the organization.");
  }

  if (children.length > 0) {
    const names = children.map((c) => `${c.name} (${c.title})`).join(", ");
    parts.push(`${names} report${children.length === 1 ? "s" : ""} to you.`);
  }

  return parts.join(" ");
}

/** Format a list of decisions the role has been informed about. */
function formatInformedDecisions(
  decisions: Decision[],
  allRoles: Role[],
): string {
  if (decisions.length === 0)
    return "No decisions have been shared with you yet.";

  return decisions
    .map((d) => {
      const author = allRoles.find((r) => r.id === d.roleId);
      const authorLabel = author
        ? `${author.name} (${author.title})`
        : "Unknown";
      return `- [${authorLabel}]: ${d.content}`;
    })
    .join("\n");
}

/** Current date in a readable format. */
function currentDate(): string {
  return new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Template functions
// ---------------------------------------------------------------------------

/**
 * Generate the first-message prompt for a ChatGPT conversation
 * that will play the given role.
 */
export function generateRolePrompt(
  role: Role,
  allRoles: Role[],
  informedDecisions: Decision[],
): string {
  const orgChartText = buildOrgChartText(allRoles);
  const reportingLine = buildReportingLine(role, allRoles);
  const decisionsText = formatInformedDecisions(informedDecisions, allRoles);

  return [
    `You are ${role.name}, ${role.title} at ${COMPANY_NAME}.`,
    "",
    "== ABOUT YOU ==",
    role.persona || "(No persona defined yet.)",
    "",
    "== YOUR RESPONSIBILITIES ==",
    role.responsibilities || "(No responsibilities defined yet.)",
    "",
    "== COMPANY STRUCTURE ==",
    "You work in a company with the following structure:",
    orgChartText,
    "",
    "== YOUR REPORTING LINE ==",
    reportingLine,
    "",
    "== WHAT YOU CURRENTLY KNOW ==",
    "The following decisions and information have been shared with you:",
    decisionsText,
    "",
    "== CURRENT STATUS ==",
    "Your current focus/notes:",
    role.notes || "(No notes yet.)",
    "",
    "== RULES ==",
    `- You ARE ${role.name}. You are a real person doing a real job. Never break character.`,
    "- Respond naturally as someone in your role would.",
    "- Reference your colleagues by name when relevant.",
    "- If asked about something outside your expertise, suggest who in the company might know.",
    "- Be proactive — suggest ideas, flag risks, and push for action items.",
    "",
    "You are now in a working session. What would you like to discuss?",
  ].join("\n");
}

/**
 * Generate an internal memo to brief one role about a decision
 * made by another role.
 */
export function generateBriefingMemo(
  sourceRole: Role,
  targetRole: Role,
  decision: Decision,
): string {
  const summary =
    decision.content.split(/[.!?]/)[0]?.trim() || decision.content;

  return [
    "--- INTERNAL MEMO ---",
    `From: ${sourceRole.name}, ${sourceRole.title}`,
    `To: ${targetRole.name}, ${targetRole.title}`,
    `Date: ${currentDate()}`,
    `Subject: ${summary}`,
    "",
    decision.content,
    "",
    "Please acknowledge this information and factor it into your current work. If this impacts your plans or timeline, let me know.",
  ].join("\n");
}

/**
 * Generate a context-reset prompt: full role prompt + a catch-up summary
 * from the previous session.
 */
export function generateContextReset(
  role: Role,
  allRoles: Role[],
  informedDecisions: Decision[],
  summary: string,
): string {
  const fullRolePrompt = generateRolePrompt(role, allRoles, informedDecisions);

  return [
    fullRolePrompt,
    "",
    "== PREVIOUS SESSION SUMMARY ==",
    "In your last working session, here is what was discussed and decided:",
    summary,
    "",
    "Pick up where you left off. What should we focus on next?",
  ].join("\n");
}

/**
 * Generate the board-level report for pasting into a Claude Code session.
 *
 * @param decisionAwareness - Map from decision ID to the set of role IDs
 *   that have been informed. Used to compute knowledge gaps.
 */
export function generateBoardReport(
  roles: Role[],
  decisions: Decision[],
  decisionAwareness: Record<string, string[]>,
): string {
  const orgChartText = buildOrgChartText(roles);

  const roleStatusList = roles
    .map((r) => `- ${r.name} (${r.title}): ${r.status}`)
    .join("\n");

  const decisionsList =
    decisions.length === 0
      ? "(No decisions recorded.)"
      : decisions
          .map((d) => {
            const author = roles.find((r) => r.id === d.roleId);
            const authorLabel = author
              ? `${author.name} (${author.title})`
              : "Unknown";
            const informed = (decisionAwareness[d.id] ?? d.informedRoleIds)
              .map((rid) => roles.find((r) => r.id === rid)?.name ?? rid)
              .join(", ");
            return `- [${authorLabel}]: ${d.content}\n  Informed: ${informed || "nobody"}`;
          })
          .join("\n");

  // Knowledge gaps: for each decision, list roles that were NOT informed.
  const gapLines: string[] = [];
  for (const d of decisions) {
    const informedSet = new Set(decisionAwareness[d.id] ?? d.informedRoleIds);
    const uninformed = roles.filter(
      (r) => r.id !== d.roleId && !informedSet.has(r.id),
    );
    if (uninformed.length > 0) {
      const summary = d.content.split(/[.!?]/)[0]?.trim() || d.content;
      const names = uninformed.map((r) => r.name).join(", ");
      gapLines.push(`- "${summary}" — uninformed: ${names}`);
    }
  }
  const knowledgeGaps =
    gapLines.length > 0
      ? gapLines.join("\n")
      : "(All roles are fully informed.)";

  return [
    "== THE COMPANY — BOARD REPORT ==",
    `Generated: ${currentDate()}`,
    "",
    "== COMPANY STRUCTURE ==",
    orgChartText,
    "",
    "== ROLE STATUSES ==",
    roleStatusList,
    "",
    "== ALL DECISIONS ==",
    decisionsList,
    "",
    "== KNOWLEDGE GAPS ==",
    "The following roles have NOT been informed about these decisions:",
    knowledgeGaps,
    "",
    "== RECENT ACTIVITY ==",
    "(See individual role exports for full conversation details.)",
    "",
    "As the Board of Directors, please analyze this company state and advise on:",
    "1. What are the biggest risks right now?",
    "2. Which roles need attention?",
    "3. What decisions need to be made?",
    "4. What briefings should be sent?",
  ].join("\n");
}

/**
 * Export a role's full conversation history for Claude Code to summarize.
 */
export function generateRoleExport(
  role: Role,
  conversations: Conversation[],
): string {
  const convoText =
    conversations.length === 0
      ? "(No conversations recorded.)"
      : conversations
          .map(
            (c, i) =>
              `--- Conversation ${i + 1} (${c.createdAt}) ---\n` +
              `Asked:\n${c.asked}\n\n` +
              `Answered:\n${c.answered}`,
          )
          .join("\n\n");

  return [
    "== CONVERSATION EXPORT ==",
    `Role: ${role.name} (${role.title})`,
    `Status: ${role.status}`,
    `Total conversations: ${conversations.length}`,
    "",
    "== CURRENT NOTES ==",
    role.notes || "(No notes.)",
    "",
    "== FULL CONVERSATION LOG ==",
    convoText,
    "",
    "Please generate a concise summary of this conversation history, focusing on:",
    "1. Key decisions made",
    "2. Action items and commitments",
    "3. Open questions or unresolved issues",
    "4. Important context for the next session",
    "",
    "Format the summary so I can paste it into this role's pinned notes.",
  ].join("\n");
}
